import { Button, Skeleton } from 'antd';
import { isEmpty } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import { cn } from '@/lib/utils';
import { formatNumberToK } from '@/shared/utils';
import { InstagramQueryPostItemModel, InstagramQueryPostsModel } from '@/types/anonymously.interface';
import styles from './index.module.less';

/** 分页加载骨架屏 - 显示在现有帖子列表下方 */
const PaginationSkeleton = ({ count = 12 }: { count?: number }) => (
  <div className={cn('grid grid-cols-3 gap-4 max-md:grid-cols-2 max-md:gap-1.5 mt-4')}>
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="aspect-square bg-gray-200 rounded-md animate-pulse" />
    ))}
  </div>
);

const LocalImage = dynamic(() => import('@/components/Common/LocalImage'), {
  ssr: false,
});

interface PostListContentProps {
  loading?: boolean;
  data?: InstagramQueryPostsModel;
  onNextClick?: () => void;
  /** 点击帖子时触发的事件，传入点击的帖子 ite */
  onItemClick?: (item: InstagramQueryPostItemModel) => void;
}

const PostListContent = ({ loading, data, onNextClick, onItemClick }: PostListContentProps) => {
  const { t } = useTranslation();

  const handleItemClick = (item: InstagramQueryPostItemModel) => {
    onItemClick && onItemClick(item);
  };

  if (!data) {
    return null;
  }

  if (!data?.count) {
    return (
      <div className={styles.noData}>
        <span>{t('The account you entered has no posts.')}</span>
        <span
          style={{
            marginLeft: '5px',
            color: '#725CFF',
            cursor: 'pointer',
          }}
          onClick={() => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }}
        >
          {t('Please try another account.')}
        </span>
      </div>
    );
  }

  return (
    <>
      <div className={cn(styles.content, 'mt-10 max-md:mt-5')}>
        <div className={styles.groupContainer}>
          {/* 帖子列表内容 */}
          <div className={cn(styles.listContainer, 'grid grid-cols-3 gap-4 max-md:!grid-cols-2 max-md:!gap-1.5')}>
            {data?.items?.map((item: InstagramQueryPostItemModel, index: number) => {
              return (
                <div
                  className={cn(styles.item, 'rounded-md overflow-hidden')}
                  key={index}
                  onClick={handleItemClick.bind(null, item)}
                >
                  <LocalImage
                    alt={typeof item.caption === 'string' ? item.caption : item?.caption?.text}
                    // 搜索时返回的图片字段是 cover_image，而分页加载时返回的图片字段是 thumbnail_url
                    src={item?.cover_image || item?.thumbnail_url}
                    style={{ width: '100%', height: '100%' }}
                  />
                  {/* 媒体内容图标 */}
                  {item?.carousel_media && item.carousel_media?.length >= 1 ? (
                    // 混合内容图标
                    <Image
                      alt=""
                      src={'/images/toolkit/img-small-icon.webp'}
                      width={20}
                      height={20}
                      loading="lazy"
                      style={{ position: 'absolute', top: '15px', right: '15px' }}
                    />
                  ) : item?.is_video ? (
                    // 视频图标
                    <Image
                      alt=""
                      src={'/images/toolkit/video-small-icon.webp'}
                      width={20}
                      height={20}
                      loading="lazy"
                      style={{ position: 'absolute', top: '15px', right: '15px' }}
                    />
                  ) : // 图片，不需要显示图标
                  null}

                  <div className={cn(styles.infoContainer, 'p-4 text-base max-md:p-2 max-md:text-sm')}>
                    <div className={styles.infoDataContainer}>
                      <Image alt="" src={'/images/toolkit/comment-icon.webp'} width={20} height={20} loading="lazy" />
                      <span className="ml-0.5 mr-2.5">{formatNumberToK(item?.comment_count)}</span>
                      <Image alt="" src={'/images/toolkit/like-icon.webp'} width={20} height={20} loading="lazy" />
                      <span className="ml-0.5">{formatNumberToK(item?.like_count)}</span>
                    </div>
                    <div>{item?.publish_time}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CLS 优化：分页加载时显示骨架屏预占位 */}
        {loading && <PaginationSkeleton />}

        {!isEmpty(data?.page_token) && (
          <Button className={styles.bottomBtn} onClick={onNextClick} loading={loading}>
            {t('Next Page').toLocaleUpperCase()}
          </Button>
        )}
      </div>
    </>
  );
};

export default PostListContent;
